import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { Button, ErrorState, Icon, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { locationsAtom } from "@/store/locationAtoms";
import { organizationsAtom } from "@/store/organizationAtoms";
import type { LocationResponse } from "@/models/location";
import {
    LocationFormModal,
    LOCATION_FORM_MODAL_ID,
} from "./_components/LocationFormModal";
import {
    DeleteLocationModal,
    DELETE_LOCATION_MODAL_ID,
} from "./_components/DeleteLocationModal";
import { UploadCertificateModal } from "./_components/UploadCertificateModal";
import { LocationStatusNotice } from "./_components/LocationStatusNotice";

const openDialog = (id: string) => {
    const modal = document.getElementById(id) as HTMLDialogElement | null;
    modal?.showModal();
};

export const AccountLocations = () => {
    const { getLocations } = useLocationApi();
    const { getMeOrganizations } = useOrganizationApi();
    const locations = useAtomValue(locationsAtom);
    const organizations = useAtomValue(organizationsAtom);
    // The status of the read THIS page issued (#407), not the shared location
    // atoms: those outlive the page (a revisit painted the last visit's failure,
    // or "No locations yet" before anything was read) and follow every
    // surface's GET /locations (the header switcher's failure replaced a list
    // this page had read). Every re-read below goes through `fetchLocations`.
    const { readStatus, reload: fetchLocations } = useMountRead(getLocations);

    const [editing, setEditing] = useState<LocationResponse | undefined>();
    const [deleting, setDeleting] = useState<LocationResponse | undefined>();
    // The location the certificate upload is open for (#437), or null.
    const [certificateFor, setCertificateFor] = useState<string | null>(null);
    const { showSuccess } = useNotification();

    // One org per customer (E12 decision) — create posts under this org.
    const organizationId = organizations[0]?.id;

    useEffect(() => {
        // Need the org id to create a location; the list endpoint is flat.
        getMeOrganizations().catch(() => {
            /* notification layer surfaces the error */
        });
    }, [getMeOrganizations]);

    const handleAdd = () => {
        setEditing(undefined);
        openDialog(LOCATION_FORM_MODAL_ID);
    };

    const handleEdit = (location: LocationResponse) => {
        setEditing(location);
        openDialog(LOCATION_FORM_MODAL_ID);
    };

    const handleDelete = (location: LocationResponse) => {
        setDeleting(location);
        openDialog(DELETE_LOCATION_MODAL_ID);
    };

    // #437: a new location is Pending with no certificate, and approval needs
    // one, so a create goes straight on to the upload for it. The upload's
    // GET /locations on open brings the new location into this list too, so
    // only an edit re-reads here (an upload re-reads once it lands, below).
    const handleSaved = (createdLocationId?: string) => {
        if (createdLocationId) {
            setCertificateFor(createdLocationId);
            return;
        }

        void fetchLocations();
    };

    // The modal closes on success; the toast carries the confirmation, as on
    // the account dashboard. The list is re-read so the row's certificate
    // state is the server's (the upload itself changes no atom).
    const handleCertificateUploaded = (locationName: string) => {
        setCertificateFor(null);
        void fetchLocations();
        showSuccess({
            message: `Resale certificate uploaded for ${locationName}. Our team will review it.`,
        });
    };

    // OB-09: the re-read moves the row to Pending, which is its new state. A
    // re-read that fails leaves the list on screen as it was (the row keeps its
    // rejection until a later read), never an error in its place, and the toast
    // confirms the resubmit on its own.
    const handleResubmitted = (locationName: string) => {
        void fetchLocations();
        showSuccess({
            message: `${locationName} resubmitted. Our team will review it.`,
        });
    };

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="text-3xl font-bold text-neutral">
                        Locations
                    </h1>
                    <Button
                        color={ComponentColor.Warning}
                        onClick={handleAdd}
                        disabled={!organizationId}
                        className="text-neutral font-bold normal-case"
                    >
                        Add Location
                    </Button>
                </div>

                {/* Only with no list in hand: a re-read after an edit, upload
                    or resubmit refreshes the list in place, and a failed one
                    leaves it on screen (PR 374 review 5). */}
                {readStatus === "loading" && locations.length === 0 && (
                    <div className="flex justify-center items-center py-12">
                        <Spinner size="lg" />
                    </div>
                )}

                {readStatus === "failed" && locations.length === 0 && (
                    <ErrorState onRetry={fetchLocations} />
                )}

                {readStatus === "loaded" && locations.length === 0 && (
                    <div className="flex flex-col items-center gap-4 py-12 text-center">
                        <p className="text-neutral/70">
                            No locations yet. Add your first location to start
                            ordering.
                        </p>
                        <Button
                            color={ComponentColor.Warning}
                            onClick={handleAdd}
                            disabled={!organizationId}
                            className="text-neutral font-bold normal-case"
                        >
                            Add Location
                        </Button>
                    </div>
                )}

                {locations.length > 0 && (
                    <ul className="flex flex-col gap-4">
                        {locations.map((location) => (
                            <li
                                key={location.id}
                                className="flex items-start justify-between gap-4 rounded-lg border border-base-300 bg-base-200 p-4"
                            >
                                <div>
                                    {/* #320: the name leads to the
                                        location's detail page. */}
                                    <p className="font-semibold text-neutral">
                                        <Link
                                            to={ROUTES.ACCOUNT_LOCATION_DETAIL(
                                                location.id,
                                            )}
                                            className="link link-hover"
                                        >
                                            {location.name}
                                        </Link>
                                    </p>
                                    <p className="text-sm text-neutral/80">
                                        {location.streetAddress}
                                    </p>
                                    <p className="text-sm text-neutral/80">
                                        {location.city}, {location.state}{" "}
                                        {location.zipCode}
                                    </p>
                                    <p className="text-sm text-neutral/80">
                                        {location.country}
                                    </p>
                                    <LocationStatusNotice
                                        location={location}
                                        onUploadCertificate={() =>
                                            setCertificateFor(location.id)
                                        }
                                        onResubmitted={() =>
                                            handleResubmitted(location.name)
                                        }
                                    />
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <Button
                                        color={ComponentColor.Primary}
                                        onClick={() => handleEdit(location)}
                                        className="text-neutral"
                                    >
                                        <Icon name="edit" />
                                        Edit
                                    </Button>
                                    <Button
                                        color={ComponentColor.Error}
                                        onClick={() => handleDelete(location)}
                                    >
                                        <Icon name="delete" />
                                        Delete
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <LocationFormModal
                location={editing}
                organizationId={organizationId}
                onSaved={handleSaved}
                onClose={() => setEditing(undefined)}
            />
            <DeleteLocationModal
                location={deleting}
                onDeleted={fetchLocations}
                onClose={() => setDeleting(undefined)}
            />
            {certificateFor && (
                <UploadCertificateModal
                    initialLocationId={certificateFor}
                    onUploaded={handleCertificateUploaded}
                    onClose={() => setCertificateFor(null)}
                />
            )}
        </div>
    );
};
